import { FC, useCallback, useMemo, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import Lottie from "react-lottie"
import { Typography } from "@eventbrite/marmalade"

// redux
import { setSearchQuery as setReduxSearchQuery } from "../../../store/slices/search_query.slice"
import { setMetaData } from "../../../store/slices/metadata.slice"

// utils
import { getFlag } from "../../../utils/query_param_flags"
import { navigateWithPrefix } from "../../../utils/general_utils"
import { renderRichText } from "../../../utils/contentful/render_rich_text"

// types
import { RootState } from "../../../store/store"
import { SingleHit } from "../../../services/help_articles.service"

// lottie data
import * as greenLoaderLottieData from "../../../icons/lottie/GreenSpinner/Progress loading_circular_Green.json"

// styles
import {
  searchItemsContainer,
  searchItem,
  suggestedheader,
  searchLoadingContainer,
  noResultsContainer
} from "./SearchResultsBox.module.scss"

export const addEmphasis = (query: string, title: string) => {
  if (!query || !title) {
    return title
  }

  const lowerTitle = title.toLowerCase()
  if (!lowerTitle.includes(query?.toLowerCase())) {
    return title
  }

  const re = new RegExp(query, "i")
  const [value] = title.match(re)
  const replacedString = title.replace(re, `<strong>${value}</strong>`)

  return replacedString
}

export const SearchResultsBox: FC<{
  query: string
  hits: SingleHit[]
  isSearchMenuOpen: boolean
  headerSearchResultsMicrocopy: Queries.HomepageDataQuery["headerSearchResults"]
}> = ({ hits, query, isSearchMenuOpen, headerSearchResultsMicrocopy }) => {
  const dispatch = useDispatch()

  const langCode = useSelector((state: RootState) => state?.metaData?.langCode)
  const isLoadingResults = useSelector(
    (state: RootState) => state?.metaData?.isLoadingResults
  )

  useEffect(() => {
    if (query) {
      dispatch(setMetaData({ isLoadingResults: true }))

      const timeoutId = setTimeout(() => {
        dispatch(setMetaData({ isLoadingResults: false }))
      }, 5000)

      return () => clearTimeout(timeoutId)
    }
  }, [query, dispatch])

  useEffect(() => {
    if (hits !== undefined) {
      dispatch(setMetaData({ isLoadingResults: false }))
    }
  }, [hits, dispatch])

  const headerSearchResultsCopy = useMemo<string>(() => {
    if (headerSearchResultsMicrocopy?.microcopyValue?.raw?.length > 0) {
      return renderRichText(
        headerSearchResultsMicrocopy?.microcopyValue?.raw
      )[0].props.children[0]
    }

    // fallback text
    return "Results for"
  }, [headerSearchResultsMicrocopy])

  const onClick = useCallback(
    (slug: string, articleId: string) => {
      if (slug?.length > 0 && articleId) {
        navigateWithPrefix(`/${langCode}/articles/${articleId}/${slug}/`)
      }
    },
    [langCode]
  )

  const isUsingSearchTermFlag = useMemo(() => {
    const flag = getFlag()

    if (flag === "searchterm") {
      return true
    }

    return false
  }, [])

  const searchOptions = useMemo(() => {
    if (!hits || hits.length === 0) {
      return null
    }

    const suggestedsearchHeader = (
      <Typography as="p" variant="body-sm" className={suggestedheader}>
        {" "}
        Suggested Articles
      </Typography>
    )

    const resultItems = hits.slice(0, 4).map((x, i) => {
      const title = x.fields?.helparticletitle
      const slug = x.fields?.slug
      const articleId = x.fields?.articleid

      return (
        <li
          key={`${x.id}__${i}`}
          className={searchItem}
          onClick={() => onClick(slug, articleId)}
        >
          {!title ? (
            <div className={searchLoadingContainer}>
              <Lottie
                options={{
                  loop: true,
                  autoplay: true,
                  animationData: greenLoaderLottieData,
                  rendererSettings: {
                    preserveAspectRatio: "xMidYMid slice"
                  }
                }}
                width={24}
                height={24}
              />
            </div>
          ) : (
            <span
              dangerouslySetInnerHTML={{
                __html: addEmphasis(query, title)
              }}
            />
          )}
        </li>
      )
    })

    return [suggestedsearchHeader, ...resultItems]
  }, [query, hits, isUsingSearchTermFlag, onClick])

  if (!isSearchMenuOpen) {
    return null
  }

  return (
    <>
      {isLoadingResults && (
        <div className={searchLoadingContainer}>
          <Lottie
            options={{
              loop: true,
              autoplay: true,
              animationData: greenLoaderLottieData,
              rendererSettings: {
                preserveAspectRatio: "xMidYMid slice"
              }
            }}
            width={24}
            height={24}
          />
        </div>
      )}
      {!isLoadingResults && hits && hits.length > 0 && (
        <ul
          id="search-autocomplete-suggestions"
          aria-label="search autocomplete"
          className={searchItemsContainer}
        >
          <Typography as="p" variant="body-md">
            {" "}
            {searchOptions}
          </Typography>
        </ul>
      )}
      {!isLoadingResults && hits && hits.length === 0 && query && (
        <div className={noResultsContainer}>
          <p>No results found for "{query}"</p>
        </div>
      )}
    </>
  )
}
